import {
  AlertCircle,
  ArrowRight, Brain,
  Calendar, CheckCircle,
  Clock,
  FileText,
  Phone,
  Shield,
  Users,
  X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { StatCard } from './components';

const StatusIndicator = ({ status }) => {
  const styles = {
    scheduled: 'bg-blue-100 text-blue-800',
    'in-progress': 'bg-yellow-100 text-yellow-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  };

  return (
    <span className={`px-2 py-1 text-xs rounded-full font-medium ${styles[status] || 'bg-gray-100'}`}>
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
};

const ResourceCard = ({ title, description, link }) => (
  <div className="bg-white p-4 rounded-lg border hover:shadow-md transition-shadow">
    <h3 className="font-medium text-gray-900 mb-1">{title}</h3>
    <p className="text-sm text-gray-500 mb-2">{description}</p>
    <a
      href={link}
      className="text-sm text-uzima-maroon-500 hover:text-uzima-maroon-600 flex items-center"
    >
      Learn more
      <ArrowRight className="h-4 w-4 ml-1" />
    </a>
  </div>
);

// Session Summary Modal Component
const SessionSummaryModal = ({ session, onClose }) => {
  if (!session) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-uzima-maroon-600 to-uzima-maroon-700 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-white">Session Summary</h2>
            <p className="text-blue-100 text-sm mt-1">
              {new Date(session.date).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
          <div className="space-y-4">
            {/* Counselor Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Counselor</p>
              <p className="font-semibold text-gray-900">{session.counselorName}</p>
            </div>

            {/* Summary */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Summary</h3>
              {session.summary ? (
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                  <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {session.summary}
                  </p>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg text-center">
                  <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    No summary available for this session yet.
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Your counselor may add a summary after reviewing the session.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export const StudentDashboard = () => {
  const { get } = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    upcomingAppointments: 0,
    completedSessions: 0,
    availableCounselors: 0,
    nextAppointment: null,
    recentSessions: [],
    urgentCareInfo: null,
    notifications: [],
    sessionProgress: {
      goalsSet: 0,
      goalsAchieved: 0,
      sessionsAttended: 0,
      totalSessions: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await get('/dashboard/stats/student');
        // Ensure we have default values for all properties
        setStats(prev => ({
          ...prev,
          ...response.data.data,
          sessionProgress: {
            ...prev.sessionProgress,
            ...(response.data.data.sessionProgress || {})
          }
        }));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    // Set up periodic refresh
    const interval = setInterval(fetchStats, 300000); // 5 minutes
    return () => clearInterval(interval);
  }, [get]);

  const handleBookAppointment = () => {
    navigate('/student/appointments/new');
  };

  const handleViewSessions = () => {
    navigate('/student/sessions');
  };

  const handleViewAppointmentDetails = (appointmentId) => {
    navigate(`/student/appointments/${appointmentId}`);
  };

  // Calculate goals percentage safely
  const calculateGoalsPercentage = () => {
    const goalsSet = stats.sessionProgress?.goalsSet || 0;
    const goalsAchieved = stats.sessionProgress?.goalsAchieved || 0;
    if (goalsSet === 0) return '0';
    return Math.round((goalsAchieved / goalsSet) * 100).toString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-uzima-maroon-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="ml-3 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Welcome Section */}
      <div>
        <h1 className="text-2xl font-bold bg-gradient-to-r from-uzima-maroon-600 to-uzima-maroon-700 bg-clip-text text-transparent">
          Welcome back, {user?.fullName || user?.username}!
        </h1>
        <p className="text-gray-500 mt-1">Track your counseling journey and upcoming sessions</p>
      </div>

      {/* Horizontal Quick Actions Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <button
          onClick={handleBookAppointment}
          className="group relative overflow-hidden bg-gradient-to-br from-uzima-maroon-500 to-uzima-maroon-600 hover:from-uzima-maroon-700 hover:to-uzima-maroon-800 text-white rounded-lg p-3 shadow-md hover:shadow-lg transition-all duration-200"
        >
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 rounded-md">
              <Calendar className="h-4 w-4" />
            </div>
            <div className="text-left flex-1">
              <div className="font-semibold text-xs">Book Appointment</div>
              <div className="text-[10px] text-blue-100 mt-0.5">Schedule session</div>
            </div>
          </div>
        </button>

        <button
          onClick={handleViewSessions}
          className="group relative overflow-hidden bg-gradient-to-br from-uzima-maroon-500 to-uzima-maroon-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-lg p-3 shadow-md hover:shadow-lg transition-all duration-200"
        >
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 rounded-md">
              <FileText className="h-4 w-4" />
            </div>
            <div className="text-left flex-1">
              <div className="font-semibold text-xs">Session History</div>
              <div className="text-[10px] text-purple-100 mt-0.5">View past sessions</div>
            </div>
          </div>
        </button>

        <button
          onClick={() => navigate('/student/resources')}
          className="group relative overflow-hidden bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-lg p-3 shadow-md hover:shadow-lg transition-all duration-200"
        >
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 rounded-md">
              <Brain className="h-4 w-4" />
            </div>
            <div className="text-left flex-1">
              <div className="font-semibold text-xs">Resources</div>
              <div className="text-[10px] text-emerald-100 mt-0.5">Mental health guides</div>
            </div>
          </div>
        </button>

        <button
          onClick={() => navigate('/student/profile')}
          className="group relative overflow-hidden bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg p-3 shadow-md hover:shadow-lg transition-all duration-200"
        >
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 rounded-md">
              <Users className="h-4 w-4" />
            </div>
            <div className="text-left flex-1">
              <div className="font-semibold text-xs">My Profile</div>
              <div className="text-[10px] text-orange-100 mt-0.5">Update info</div>
            </div>
          </div>
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-5 border border-gray-200 hover:shadow-md hover:border-gray-300 transition-all duration-200">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Calendar className="h-5 w-5 text-blue-600" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">{stats.upcomingAppointments || 0}</div>
            </div>
          </div>
          <h3 className="text-sm font-semibold text-gray-700">Upcoming Appointments</h3>
          <p className="text-xs text-gray-500 mt-1">Scheduled sessions</p>
        </div>

        <div className="bg-white rounded-lg p-5 border border-gray-200 hover:shadow-md hover:border-gray-300 transition-all duration-200">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <CheckCircle className="h-5 w-5 text-blue-600" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">{stats.completedSessions || 0}</div>
            </div>
          </div>
          <h3 className="text-sm font-semibold text-gray-700">Sessions Completed</h3>
          <p className="text-xs text-gray-500 mt-1">Total sessions attended</p>
        </div>

        <div className="bg-white rounded-lg p-5 border border-gray-200 hover:shadow-md hover:border-gray-300 transition-all duration-200">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">{stats.availableCounselors || 0}</div>
            </div>
          </div>
          <h3 className="text-sm font-semibold text-gray-700">Available Counselors</h3>
          <p className="text-xs text-gray-500 mt-1">Ready to help</p>
        </div>

        <div className="bg-white rounded-lg p-5 border border-gray-200 hover:shadow-md hover:border-gray-300 transition-all duration-200">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Brain className="h-5 w-5 text-blue-600" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">{calculateGoalsPercentage()}%</div>
            </div>
          </div>
          <h3 className="text-sm font-semibold text-gray-700">Goals Progress</h3>
          <p className="text-xs text-gray-500 mt-1">{stats.sessionProgress?.goalsAchieved || 0}/{stats.sessionProgress?.goalsSet || 0} goals achieved</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Next Appointment */}
          {stats.nextAppointment && (
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-200">
              <div className="bg-gray-50 px-6 py-3 flex justify-between items-center border-b border-gray-200">
                <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-blue-600" />
                  Next Appointment
                </h2>
                <StatusIndicator status={stats.nextAppointment.status} />
              </div>
              <div className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-base font-semibold text-gray-900 mb-3">
                      {stats.nextAppointment.counselorName}
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        {new Date(stats.nextAppointment.date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="h-4 w-4 mr-2 text-gray-400" />
                        {stats.nextAppointment.timeSlot}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <FileText className="h-4 w-4 mr-2 text-gray-400" />
                        {stats.nextAppointment.serviceType} • {stats.nextAppointment.sessionType}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleViewAppointmentDetails(stats.nextAppointment._id)}
                    className="px-4 py-2 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Recent Sessions */}
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-600" />
                Recent Sessions
              </h2>
            </div>
            <div className="p-6">
              {stats.recentSessions?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recentSessions.map(session => (
                    <div
                      key={session._id}
                      className="group flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-all duration-200"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-1.5 bg-blue-50 rounded">
                          <FileText className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{session.counselorName}</p>
                          <div className="flex items-center text-xs text-gray-500 mt-0.5">
                            <Clock className="h-3 w-3 mr-1" />
                            {new Date(session.date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedSession(session)}
                        className="px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-blue-200 hover:border-blue-600"
                      >
                        View Summary
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No recent sessions</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Urgent Care Info */}
          {stats.urgentCareInfo && (
            <div className="bg-white rounded-lg shadow border-2 border-red-200 overflow-hidden">
              <div className="bg-red-50 px-6 py-3 border-b border-red-200">
                <h2 className="text-base font-semibold text-red-700 flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Urgent Care
                </h2>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 bg-red-50 text-red-700 px-3 py-2 rounded-md mb-4">
                  <Clock className="h-4 w-4 flex-shrink-0" />
                  <span className="font-medium text-xs">24/7 Support Available</span>
                </div>
                <p className="text-xs text-gray-700 mb-4 leading-relaxed">
                  {stats.urgentCareInfo.message}
                </p>
                <div className="space-y-2">
                  <p className="font-semibold text-gray-900 text-xs mb-2">Emergency Contacts:</p>
                  {stats.urgentCareInfo.contacts?.map((contact, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-md border border-gray-200"
                    >
                      <Phone className="h-3 w-3 text-red-600 flex-shrink-0" />
                      <span className="text-xs text-gray-700">{contact}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Mental Health Resources */}
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Brain className="h-4 w-4 text-blue-600" />
                Mental Health Resources
              </h2>
            </div>
            <div className="p-5 space-y-2">
              <a
                href="/student/resources"
                className="group block p-3 bg-gradient-to-br from-uzima-maroon-50 to-uzima-white-50 hover:from-uzima-maroon-100 hover:to-uzima-white-100 rounded-lg border border-blue-100 hover:border-blue-200 transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1 bg-blue-100 rounded">
                      <Brain className="h-3 w-3 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 text-xs">Self-Care Guide</h3>
                      <p className="text-[10px] text-gray-600">Mental well-being tips</p>
                    </div>
                  </div>
                  <ArrowRight className="h-3 w-3 text-blue-500 group-hover:translate-x-1 transition-transform" />
                </div>
              </a>

              <a
                href="/student/resources"
                className="group block p-3 bg-gradient-to-br from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 rounded-lg border border-purple-100 hover:border-purple-200 transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1 bg-purple-100 rounded">
                      <Shield className="h-3 w-3 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 text-xs">Stress Management</h3>
                      <p className="text-[10px] text-gray-600">Effective strategies</p>
                    </div>
                  </div>
                  <ArrowRight className="h-3 w-3 text-purple-500 group-hover:translate-x-1 transition-transform" />
                </div>
              </a>

              <a
                href="/student/resources"
                className="group block p-3 bg-gradient-to-br from-emerald-50 to-green-50 hover:from-emerald-100 hover:to-green-100 rounded-lg border border-emerald-100 hover:border-emerald-200 transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1 bg-emerald-100 rounded">
                      <Users className="h-3 w-3 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 text-xs">Support Groups</h3>
                      <p className="text-[10px] text-gray-600">Peer support & workshops</p>
                    </div>
                  </div>
                  <ArrowRight className="h-3 w-3 text-emerald-500 group-hover:translate-x-1 transition-transform" />
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Session Summary Modal */}
      {selectedSession && (
        <SessionSummaryModal
          session={selectedSession}
          onClose={() => setSelectedSession(null)}
        />
      )}
    </div>
  );
};

export default StudentDashboard;